import React, { useState } from 'react';
import {
	Video,
	Camera,
	Mic,
	Monitor,
	Eye,
	Smartphone,
	AlertTriangle,
	Printer,
	Expand,
	Shield,
	Clipboard,
	Lock,
	Bell,
	Settings,
	Brain,
	Users,
	UserCheck,
	FileText,
	Upload,
	Download,
	Cpu,
	MessageCircle,
	Send,
	CheckCircle,
	Play,
	Clock,
	ArrowRight,
	Star,
	File
} from 'lucide-react';

import './get-started.scss';
import { useTranslation } from 'react-i18next';

const GetStarted = () => {
	const [activeTab, setActiveTab] = useState('recruiter');
	const { t } = useTranslation();

	const securityFeatures = [
		{
			category: 'recording_options',
			features: [
				{ key: 'record_video', icon: Video, title: 'Record Video', descriptionKey: 'rec_opt' },
				{ key: 'record_room', icon: Camera, title: 'Record Room', descriptionKey: 'rec_roo' },
				{ key: 'record_audio', icon: Mic, title: 'Record Audio', descriptionKey: 'rec_aud' },
				{ key: 'record_screen', icon: Monitor, title: 'Record Screen', descriptionKey: 'rec_scr' },
				{ key: 'track_location', icon: Eye, title: 'Track Location', descriptionKey: 'tra_loc' },
				{ key: 'mobile_proctoring', icon: Smartphone, title: 'Mobile Proctoring', descriptionKey: 'mob_pro' }
			]
		},
		{
			category: 'lockdown_options',
			features: [
				{ key: 'detect_unfocus', icon: AlertTriangle, title: 'Detect Unfocus', descriptionKey: 'det_unf' },
				{ key: 'disable_printing', icon: Printer, title: 'Disable Printing', descriptionKey: 'dis_pri' },
				{ key: 'force_full_screen', icon: Monitor, title: 'Force Full Screen', descriptionKey: 'for_ful' },
				{ key: 'detect_resizing_of_window', icon: Expand, title: 'Detect Resizing of Window', descriptionKey: 'det_res' },
				{ key: 'disable_right_click', icon: Shield, title: 'Disable Right Click', descriptionKey: 'dis_rig' },
				{ key: 'disable_clipboard', icon: Clipboard, title: 'Disable Clipboard', descriptionKey: 'dis_cli' },
				{ key: 'force_closure', icon: Lock, title: 'Force Closure', descriptionKey: 'for_clo' },
				{ key: 'camera_view', icon: Camera, title: 'Camera View', descriptionKey: 'cam_vie' },
				{ key: 'notify', icon: Bell, title: 'Notify', descriptionKey: 'not_not' },
				{ key: 'disable_keyboard_shortcuts', icon: Settings, title: 'Disable Keyboard Shortcuts', descriptionKey: 'dis_key' }
			]
		},
		{
			category: 'ai_options',
			features: [
				{ key: 'no_person_detection', icon: Brain, title: 'No Person Detection', descriptionKey: 'no_per' },
				{ key: 'object_detection', icon: Eye, title: 'Object Detection', descriptionKey: 'obj_det' },
				{ key: 'multiple_people_detection', icon: Users, title: 'Multiple People Detection', descriptionKey: 'mul_peo' }
			]
		},
		{
			category: 'verification_options',
			features: [
				{ key: 'verify_candidate', icon: UserCheck, title: 'Verify Candidate', descriptionKey: 'ver_can' },
				{ key: 'verify_id', icon: FileText, title: 'Verify ID', descriptionKey: 'ver_id' },
				{ key: 'verify_desktop', icon: Monitor, title: 'Verify Desktop', descriptionKey: 'ver_des' },
				{ key: 'verify_upload_speed', icon: Upload, title: 'Verify Upload Speed', descriptionKey: 'ver_upl' },
				{ key: 'verify_download_speed', icon: Download, title: 'Verify Download Speed', descriptionKey: 'ver_dow' },
				{ key: 'verify_ram', icon: Cpu, title: 'Verify RAM', descriptionKey: 'ver_ram' },
				{ key: 'verify_multiple_devices', icon: Smartphone, title: 'Verify Multiple Devices', descriptionKey: 'ver_mul' }
			]
		},
		{
			category: 'in_quiz_tools',
			features: [
				{ key: 'chat_bot', icon: MessageCircle, title: 'Chat Bot', descriptionKey: 'cha_bot' },
				{ key: 'live_chat', icon: Users, title: 'Live Chat', descriptionKey: 'liv_cha' }
			]
		}
	];

	const recruiterSteps = [
		{
			title: 'manage_candidates',
			description: 'manage_candidates_desc',
			step: 1,
			icon: Users
		},
		{
			title: 'create_assessment',
			description: 'create_assessment_desc',
			step: 2,
			icon: Settings
		},
		{
			title: 'send_invitations',
			description: 'send_invitations_desc',
			step: 3,
			icon: Send
		},
		{
			title: 'track_sessions',
			description: 'track_sessions_desc',
			step: 4,
			icon: Monitor
		},
		{
			title: 'organize_playlists',
			description: 'organize_playlists_desc',
			step: 5,
			icon: File
		},
		{
			title: 'assign_panels',
			description: 'assign_panels_desc',
			step: 6,
			icon: Star
		},
		{
			title: 'review_results',
			description: 'review_results_desc',
			step: 7,
			icon: FileText
		}
	];

	const candidateSteps = [
		{
			title: 'receive_invitation',
			description: 'receive_invitation_desc',
			step: 1,
			icon: Send
		},
		{
			title: 'system_check',
			description: 'system_check_desc',
			step: 2,
			icon: CheckCircle
		},
		{
			title: 'take_assessment',
			description: 'take_assessment_desc',
			step: 3,
			icon: Play
		},
		{
			title: 'submit_and_wait',
			description: 'submit_and_wait_desc',
			step: 4,
			icon: Clock
		}
	];

	return (
		<div className="how-it-works">
			<div className="how-it-works__container">
				<div className="how-it-works__header">
					<h1 className="how-it-works__title">
						{t('how_mereos_platform_works')}
					</h1>
					<p className="how-it-works__description">
						{t('comprehensive_online_assessment')}
					</p>
				</div>

				<div className="how-it-works__tabs">
					<div className="how-it-works__tab-container">
						<button
							onClick={() => setActiveTab('recruiter')}
							className={`how-it-works__tab ${activeTab === 'recruiter' ? 'how-it-works__tab--active recruiter' : ''}`}
						>
							<Users className="how-it-works__tab-icon" />
							{t('for_recruiters')}
						</button>
						<button
							onClick={() => setActiveTab('candidate')}
							className={`how-it-works__tab ${activeTab === 'candidate' ? 'how-it-works__tab--active candidate' : ''}`}
						>
							<UserCheck className="how-it-works__tab-icon" />
							{t('for_candidates')}
						</button>
					</div>
				</div>

				{activeTab === 'recruiter' && (
					<div className="how-it-works__content">
						<div className="how-it-works__steps">
							{recruiterSteps.map((step, index) => (
								<div key={step.step} className="how-it-works__step-wrapper">
									<div className="how-it-works__step how-it-works__step--recruiter">
										<div className="how-it-works__step-header">
											<div className="how-it-works__step-icon how-it-works__step-icon--recruiter">
												<step.icon />
											</div>
										</div>
										<h3 className="how-it-works__step-title">{t(step.title)}</h3>
										<p className="how-it-works__step-description">{t(step.description)}</p>
									</div>
								</div>
							))}
						</div>

						<div className="how-it-works__security">
							<h2 className="how-it-works__security-title">
								{t('advanced_security_proctoring_features')}
							</h2>

							{securityFeatures.map((section, sectionIndex) => (
								<div key={sectionIndex} className="how-it-works__security-section">
									<h3 className="how-it-works__security-subtitle">{t(section.category)}</h3>
									<div className="how-it-works__security-grid">
										{section.features.map((feature, index) => (
											<div key={index} className="how-it-works__security-item">
												<div className="how-it-works__security-icon">
													<feature.icon />
												</div>
												<div className="how-it-works__security-content">
													<h4 className="how-it-works__security-item-title">{t(feature.key)}</h4>
													<p className="how-it-works__security-item-description">{t(feature.descriptionKey)}</p>
												</div>
											</div>
										))}
									</div>
								</div>
							))}
						</div>

					</div>
				)}

				{activeTab === 'candidate' && (
					<div className="how-it-works__content">
						<div className="how-it-works__steps">
							{candidateSteps.map((step, index) => (
								<div key={step.step} className="how-it-works__step-wrapper">
									<div className="how-it-works__step how-it-works__step--candidate">
										<div className="how-it-works__step-header">
											<div className="how-it-works__step-icon how-it-works__step-icon--candidate">
												<step.icon />
											</div>
											<span className="how-it-works__step-number how-it-works__step-number--candidate">
												{step.step}
											</span>
										</div>
										<h3 className="how-it-works__step-title">{t(step.title)}</h3>
										<p className="how-it-works__step-description">{t(step.description)}</p>
									</div>
									{index < candidateSteps.length - 1 && (
										<div className="how-it-works__arrow how-it-works__arrow--candidate">
											<ArrowRight />
										</div>
									)}
								</div>
							))}
						</div>

						<div className="how-it-works__guidelines">
							<h2 className="how-it-works__guidelines-title">
								{t('what_to_expect_during_your_assessment')}
							</h2>
							<div className="how-it-works__guidelines-grid">
								<div className="how-it-works__guidelines-section">
									<h3 className="how-it-works__guidelines-section-title">{t('before_you_start')}</h3>
									<ul className="how-it-works__guidelines-list">
										<li className="how-it-works__guidelines-item">
											<CheckCircle className="how-it-works__guidelines-icon" />
											<span>{t('ensure_stable_internet_connection')}</span>
										</li>
										<li className="how-it-works__guidelines-item">
											<CheckCircle className="how-it-works__guidelines-icon" />
											<span>{t('test_your_camera_and_microphone')}</span>
										</li>
										<li className="how-it-works__guidelines-item">
											<CheckCircle className="how-it-works__guidelines-icon" />
											<span>{t('close_all_unnecessary_applications')}</span>
										</li>
										<li className="how-it-works__guidelines-item">
											<CheckCircle className="how-it-works__guidelines-icon" />
											<span>{t('find_a_quiet_well_lit_environment')}</span>
										</li>
									</ul>
								</div>
								<div className="how-it-works__guidelines-section">
									<h3 className="how-it-works__guidelines-section-title">{t('during_the_test')}</h3>
									<ul className="how-it-works__guidelines-list">
										<li className="how-it-works__guidelines-item">
											<Shield className="how-it-works__guidelines-icon how-it-works__guidelines-icon--blue" />
											<span>{t('your_screen_may_be_recorded')}</span>
										</li>
										<li className="how-it-works__guidelines-item">
											<Video className="how-it-works__guidelines-icon how-it-works__guidelines-icon--blue" />
											<span>{t('camera_will_monitor_your_behavior')}</span>
										</li>
										<li className="how-it-works__guidelines-item">
											<Brain className="how-it-works__guidelines-icon how-it-works__guidelines-icon--blue" />
											<span>{t('ai_will_detect_suspicious_activities')}</span>
										</li>
										<li className="how-it-works__guidelines-item">
											<Clock className="how-it-works__guidelines-icon how-it-works__guidelines-icon--blue" />
											<span>{t('respect_time_limits_for_each_section')}</span>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
				)}

				<div className="how-it-works__cta">
					<div className="how-it-works__cta-content">
						<h2 className="how-it-works__cta-title">{t('ready_to_get_started')}</h2>
						<p className="how-it-works__cta-description">
							{t('join_thousands_of_organizations')}
						</p>
						<div className="how-it-works__cta-buttons">
							<a href='https://mereos.eu/contact' target='_blank'>
								<button className="how-it-works__cta-button how-it-works__cta-button--primary">
									{t('contact_us')}
								</button>
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default GetStarted;